import { useQuery } from "@tanstack/react-query";
import { PLACES_AUTOCOMPLETE_MIN_CHARS } from "~/lib/places-autocomplete-constants";
import { QUERY_KEYS } from "./queryKeys";
import type { MapsCallSource } from "~/lib/maps/attribution";
import {
  getPlacesAutocomplete,
  PlacePrediction,
  type PlacesAutocompleteProfile,
} from "~/lib/server-functions/get-places-autocomplete";

export { PLACES_AUTOCOMPLETE_MIN_CHARS } from "~/lib/places-autocomplete-constants";

export type PlacesAutocompleteQueryData = {
  predictions: PlacePrediction[];
  /** True when the server returned memory- or DB-cache results (Google was not called). */
  servedFromCache: boolean;
  /** True when the shared outbound budget rejected the request (Google was not called). */
  blockedByBudget: boolean;
  /** Server proof that this live session may terminate with Place Details. */
  sessionEligible: boolean;
};

export type UsePlacesAutocompleteOptions = {
  /** Callsite for cost attribution; required so every call is attributable. */
  source: MapsCallSource;
  sessionToken?: string;
  profile?: PlacesAutocompleteProfile;
  /**
   * Gates *fetching* only. Callers must gate through this instead of blanking
   * the query string: blanking changes the query key, so an already fetched
   * suggestion list disappears from the UI the moment fetching is disabled.
   */
  enabled?: boolean;
};

export const buildPlacesAutocompleteQueryOptions = (
  query: string,
  options: UsePlacesAutocompleteOptions,
) => {
  const trimmedQuery = query.trim();
  return {
    queryKey: [
      QUERY_KEYS.PLACES_AUTOCOMPLETE,
      trimmedQuery,
      options.sessionToken,
      options.profile ?? "regions",
    ],
    queryFn: async (): Promise<PlacesAutocompleteQueryData> => {
      if (trimmedQuery.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
        return {
          predictions: [],
          servedFromCache: true,
          blockedByBudget: false,
          sessionEligible: false,
        };
      }
      const result = await getPlacesAutocomplete({
        data: {
          query: trimmedQuery,
          sessionToken: options.sessionToken,
          profile: options.profile,
          source: options.source,
        },
      });
      return {
        predictions: result.predictions,
        servedFromCache: result.servedFromCache,
        blockedByBudget: result.blockedByBudget,
        sessionEligible: result.sessionEligible,
      };
    },
    enabled:
      (options.enabled ?? true) &&
      trimmedQuery.length >= PLACES_AUTOCOMPLETE_MIN_CHARS,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    // A failed Autocomplete request is still a billed request; never auto-retry.
    retry: false as const,
  };
};

export const usePlacesAutocomplete = (
  query: string,
  options: UsePlacesAutocompleteOptions,
) => {
  return useQuery<PlacesAutocompleteQueryData>(
    buildPlacesAutocompleteQueryOptions(query, options),
  );
};
